<script setup lang="ts">
import { computed, ref } from "vue";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import type { CardQuestion, ChartCard, ResultsCard } from "@/types";
import OptionRow from "./OptionRow.vue";
import { Select } from "@/components/episto-ui/select";
import { closedQuestion, isMultipleChoiceCard, useAnalysisContext } from "../analysisData";

const props = withDefaults(
  defineProps<{
    card: Pick<ResultsCard, "id" | "content">;
    contentOnPick?: (meta: ChartCard | null) => Record<string, unknown>;
  }>(),
  { contentOnPick: () => ({}) },
);

const cardsStore = useResultsV2CardsStore();
const { availableCards, displayLocale } = useAnalysisContext();

const multipleCards = computed<ChartCard[]>(() => (availableCards.value as ChartCard[]).filter(isMultipleChoiceCard));

const questionOptions = computed(() => multipleCards.value.map((c) => ({ value: c.token, label: c.name || c.token })));

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

function setContent(patch: Record<string, unknown>) {
  cardsStore.updateCardLocal(props.card.id, { content: { ...props.card.content, ...patch } });
}

function itemQuestions(meta: ChartCard | null | undefined): CardQuestion[] {
  const questions = (meta?.questions || []).filter((q) => q.item_token);
  return questions.length > 1 ? questions : [];
}

const questionId = computed<number | null>(() => (props.card.content?.question_id as number | undefined) || null);

const questionMeta = computed<ChartCard | null>(
  () => multipleCards.value.find((c) => (c.questions || []).some((q) => q.id === questionId.value)) || null,
);

// A matrix card is remembered until its item sets the question.
const pickedToken = ref<string | null>(null);

function pickCard(token: string | null) {
  const picked = multipleCards.value.find((c) => c.token === token) || null;
  const items = itemQuestions(picked);
  setContent({ question_id: items.length ? null : (closedQuestion(picked)?.id ?? null), ...props.contentOnPick(picked) });
  pickedToken.value = token;
}

const cardToken = computed(() => questionMeta.value?.token ?? pickedToken.value);
const cardMeta = computed(() => multipleCards.value.find((c) => c.token === cardToken.value) || null);

const itemOptions = computed(() =>
  itemQuestions(cardMeta.value).map((q, idx) => ({ value: q.id, label: resolveTitle(q.item_title) || `Item ${idx + 1}` })),
);

const pickItem = (id: number | null) => setContent({ question_id: id, ...props.contentOnPick(cardMeta.value) });
</script>

<template>
  <OptionRow label="Multiple-choice question">
    <Select
      :model-value="cardToken"
      :options="questionOptions"
      placeholder="Select a multiple-choice question..."
      filterable
      @update:model-value="pickCard($event as string | null)" />
  </OptionRow>
  <OptionRow
    v-if="itemOptions.length"
    label="Item"
    nested>
    <Select
      :model-value="questionId"
      :options="itemOptions"
      placeholder="Item..."
      filterable
      @update:model-value="pickItem($event as number | null)" />
  </OptionRow>
</template>
